<template>
  <form
    class="message-composer"
    :class="{ 'drop-active': dropActive }"
    @submit.prevent="submit"
    @dragenter.prevent="handleDragEnter"
    @dragover.prevent="handleDragOver"
    @dragleave.prevent="handleDragLeave"
    @drop.prevent="handleDrop"
    @paste="handlePaste"
  >
    <div v-if="quoteMessage" class="quote-preview">
      <span>
        <strong>{{ language.t('composer.quote') }}</strong>
        {{ quoteMessage.quoteText || quoteMessage.text }}
      </span>
      <button type="button" :title="language.t('composer.cancelQuote')" @click="$emit('cancel-quote')">×</button>
    </div>
    <div class="composer-tools">
      <button type="button" :title="language.t('composer.emoji')" :aria-label="language.t('composer.emoji')" :disabled="disabled" @click="toggleEmojiPicker">☺</button>
      <button
        type="button"
        class="composer-tool-button image-tool"
        :title="language.t('composer.image')"
        :aria-label="language.t('composer.image')"
        :disabled="disabled"
        @click="chooseImage"
      >
        <svg class="composer-tool-icon" viewBox="0 0 24 24" aria-hidden="true">
          <rect x="3" y="5" width="18" height="14" rx="2.5"></rect>
          <circle cx="8.2" cy="9.2" r="1.6"></circle>
          <path d="M6.5 17l4.2-4.7 3.1 3.4 1.8-2 2.9 3.3"></path>
        </svg>
      </button>
      <button
        type="button"
        class="composer-tool-button file-tool"
        :title="language.t('composer.file')"
        :aria-label="language.t('composer.file')"
        :disabled="disabled"
        @click="chooseFile"
      >
        <svg class="composer-tool-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M7 3.8h7.2L19 8.6V20a1.8 1.8 0 0 1-1.8 1.8H7A1.8 1.8 0 0 1 5.2 20V5.6A1.8 1.8 0 0 1 7 3.8z"></path>
          <path d="M14 4v5h5"></path>
          <path d="M8.5 13h7"></path>
          <path d="M8.5 16.5h5.2"></path>
        </svg>
      </button>
      <button type="button" class="composer-tool-button gift-tool" title="礼物" aria-label="礼物" :disabled="disabled" @click="$emit('send-gift')">
        <svg class="composer-tool-icon" viewBox="0 0 24 24" aria-hidden="true">
          <rect x="3" y="8" width="18" height="4" rx="1"></rect>
          <path d="M5 12v9h14v-9M12 8v13M12 8H8a3 3 0 1 1 3-3l1 3zm0 0h4a3 3 0 1 0-3-3l-1 3z"></path>
        </svg>
      </button>
      <div v-if="canVoiceCall" class="composer-call-actions">
        <button
          type="button"
          class="composer-tool-button composer-call-button"
          :title="language.t('composer.voiceCall')"
          :aria-label="language.t('composer.voiceCall')"
          @click="$emit('voice-call')"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6.7 3.8h3.1l1.5 4.1-2.1 1.6a14 14 0 0 0 5.3 5.3l1.6-2.1 4.1 1.5v3.1a2 2 0 0 1-2.2 2A16 16 0 0 1 4.7 6a2 2 0 0 1 2-2.2z"></path>
          </svg>
        </button>
        <button
          type="button"
          class="composer-tool-button composer-call-button composer-video-call-button"
          :title="language.t('composer.videoCall')"
          :aria-label="language.t('composer.videoCall')"
          @click="$emit('video-call')"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <rect x="3" y="6" width="12" height="12" rx="2"></rect>
            <path d="m15 10 6-3v10l-6-3z"></path>
          </svg>
        </button>
      </div>
      <input ref="imageInput" type="file" accept="image/*" multiple @change="selectAttachment($event, 'image')">
      <input ref="fileInput" type="file" multiple @change="selectAttachment($event, 'file')">
      <div v-if="emojiOpen" class="emoji-picker" @click.stop>
        <button
          v-for="emoji in emojiList"
          :key="emoji"
          type="button"
          @click="insertEmoji(emoji)"
        >
          {{ emoji }}
        </button>
      </div>
    </div>
    <textarea
      v-if="!pendingVoice"
      ref="textareaEl"
      v-model="text"
      :disabled="disabled"
      :placeholder="language.t('composer.placeholder')"
      rows="3"
      @keydown.enter.exact.prevent="submit"
    ></textarea>
    <div v-if="pendingVoice" class="voice-send-preview">
      <audio class="voice-send-preview-audio" controls :src="pendingVoice.url" preload="metadata"></audio>
      <button
        type="button"
        class="voice-send-preview-discard"
        :title="language.t('composer.voiceRedo')"
        :aria-label="language.t('composer.voiceRedo')"
        @click="discardPendingVoice"
      >
        <img :src="voicePreviewRemoveIcon" alt="" aria-hidden="true" class="voice-send-preview-discard-icon">
      </button>
    </div>
    <div v-if="voiceError" class="voice-recording-error" role="alert">
      <span>{{ voiceError }}</span>
      <button type="button" aria-label="关闭提示" @click="voiceError = ''">×</button>
    </div>
    <div v-if="voiceNotice" class="voice-recording-notice" role="status">{{ voiceNotice }}</div>
    <div class="composer-footer" :class="{ 'voice-preview-footer': pendingVoice }">
      <span v-if="!pendingVoice" class="composer-hint" :class="{ recording: voiceRecording || voiceStarting || dictationStarting || dictationRecording || speechRecognitionActive || dictationProcessing }" aria-live="polite">
        {{ voiceButtonHint }}
      </span>
      <div class="composer-send-controls">
        <button
          v-if="!pendingVoice"
          type="button"
          class="voice-record-button"
          :class="{ recording: voiceRecording || voiceStarting || dictationStarting || dictationRecording || speechRecognitionActive || dictationProcessing }"
          :disabled="disabled || Boolean(pendingVoice) || dictationProcessing"
          :title="language.t('composer.voice')"
          :aria-label="language.t('composer.voice')"
          @pointerdown.prevent="beginVoicePress"
          @pointerup.prevent="endVoicePress"
          @pointercancel="cancelVoicePress"
          @contextmenu.prevent
          @keydown.enter.prevent="beginVoicePress"
          @keyup.enter.prevent="endVoicePress"
          @keydown.space.prevent="beginVoicePress"
          @keyup.space.prevent="endVoicePress"
          @click="handleVoiceClick"
        >
          <svg class="voice-record-icon" viewBox="0 0 24 24" aria-hidden="true">
            <rect x="9" y="3" width="6" height="12" rx="3"></rect>
            <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v3m-4 0h8"></path>
          </svg>
        </button>
        <button
          class="send-button"
          :class="{ 'send-voice-button': pendingVoice }"
          :disabled="disabled || (!pendingVoice && !text.trim())"
        >
          {{ language.t(pendingVoice ? 'composer.sendVoice' : 'composer.send') }}
        </button>
      </div>
    </div>
    <Teleport to="body">
      <div v-if="pendingImages.length" class="image-send-preview-backdrop" :class="{ 'drop-active': previewDropActive }">
        <section class="image-send-preview" aria-label="图片发送预览">
          <header><strong>发送图片</strong><button type="button" class="image-preview-close" title="取消发送" @click="cancelImagePreview">×</button></header>
          <div class="image-preview-stage"><img :src="activePendingImage.url" :alt="activePendingImage.file.name || '待发送图片'"></div>
          <div class="image-preview-caption-row"><textarea v-model="previewCaption" rows="1" placeholder="添加图片说明"></textarea></div>
          <footer>
            <div class="image-preview-thumbnails">
              <button v-for="(item, index) in pendingImages" :key="item.url" type="button" class="image-preview-thumbnail" :class="{ active: index === activeImageIndex }" @click="activeImageIndex = index">
                <img :src="item.url" :alt="item.file.name || '待发送图片'">
                <span class="image-preview-remove" title="移除" @click.stop="removePendingImage(index)">×</span>
              </button>
              <button type="button" class="image-preview-add" title="继续添加图片" @click="chooseImage">+</button>
            </div>
            <button type="button" class="image-preview-send" :disabled="disabled" title="发送图片" @click="sendPendingImages">➤</button>
          </footer>
        </section>
      </div>
    </Teleport>
  </form>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import voicePreviewRemoveIcon from '@/assets/icons/voice-preview-remove.svg';
import { useLanguageStore } from '@/stores/language';
import { useSessionStore } from '@/stores/session';
import { transcribeVoiceMessage } from '@/services/messageTranslation';

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false
  },
  quoteMessage: {
    type: Object,
    default: null
  },
  canVoiceCall: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['send', 'send-attachment', 'send-voice', 'send-gift', 'cancel-quote', 'voice-call', 'video-call']);
const language = useLanguageStore();
const session = useSessionStore();
const text = ref('');
const emojiOpen = ref(false);
const dropActive = ref(false);
const textareaEl = ref(null);
const imageInput = ref(null);
const fileInput = ref(null);
const pendingImages = ref([]);
const activeImageIndex = ref(0);
const previewCaption = ref('');
const previewDropActive = ref(false);
const voiceStarting = ref(false);
const voiceRecording = ref(false);
const speechRecognitionActive = ref(false);
const dictationStarting = ref(false);
const dictationRecording = ref(false);
const dictationProcessing = ref(false);
const pendingVoice = ref(null);
const voiceDurationLabel = ref('00:00');
const voiceError = ref('');
const voiceNotice = ref('');
const activePendingImage = computed(() => pendingImages.value[activeImageIndex.value] || pendingImages.value[0]);
let voiceStream = null;
let voiceRecorder = null;
let voiceChunks = [];
let voiceStartedAt = 0;
let voicePointerHeld = false;
let voicePointerId = null;
let voiceShouldPreview = false;
let voiceClock = null;
let voiceMaxTimer = null;
let voiceStopReason = '';
let voicePressTimer = null;
let voicePressHeld = false;
let voicePressStartedRecording = false;
let voicePressPointerId = null;
let voicePressKind = '';
let ignoreKeyboardVoiceClick = false;
let speechRecognition = null;
let dictationStream = null;
let dictationRecorder = null;
let dictationChunks = [];
let dictationStartedAt = 0;
let dictationMaxTimer = null;
let dictationAbortController = null;
let dictationStartCancelled = false;
let dictationCaptureCancelled = false;
let dictationSourceLanguage = '';
let dragDepth = 0;
const VOICE_HOLD_DURATION = 420;
const DEVICE_STT_LANGUAGES = {
  zh_hans: 'zh-CN',
  zh_hant: 'zh-TW',
  en: 'en-US',
  th: 'th-TH'
};
const voiceButtonHint = computed(() => {
  if (voiceRecording.value || voiceStarting.value) {
    return language.t('composer.voiceRecording', { duration: voiceDurationLabel.value });
  }
  if (speechRecognitionActive.value) return language.t('composer.dictationListening');
  if (dictationStarting.value) return language.t('composer.dictationStarting');
  if (dictationRecording.value) return language.t('composer.dictationRecording');
  if (dictationProcessing.value) return language.t('composer.dictationProcessing');
  return language.t('composer.shortcut');
});
const emojiList = [
  '😀', '😄', '😂', '😊', '😍', '😘', '😎', '😢',
  '😡', '👍', '🙏', '👏', '💪', '🎉', '❤️', '🔥',
  '🌹', '☕', '✅', '⭐', '💰', '🎁', '🤝', '👌'
];

function submit() {
  if (pendingVoice.value) {
    sendPendingVoice();
    return;
  }
  const value = text.value.trim();
  if (!value) {
    return;
  }
  emit('send', value);
  text.value = '';
  emojiOpen.value = false;
}

function beginVoicePress(event) {
  if (props.disabled || pendingVoice.value || dictationProcessing.value || voicePressHeld) return;
  voiceError.value = '';
  voiceNotice.value = '';
  voicePressHeld = true;
  voicePressStartedRecording = false;
  voicePressPointerId = event?.pointerId ?? null;
  voicePressKind = event?.type?.startsWith('key') ? 'keyboard' : 'pointer';
  if (voicePressPointerId !== null) {
    event.currentTarget?.setPointerCapture?.(voicePressPointerId);
  }
  voicePressTimer = window.setTimeout(() => {
    voicePressTimer = null;
    if (!voicePressHeld) return;
    voicePressStartedRecording = true;
    startVoiceRecording(event);
  }, VOICE_HOLD_DURATION);
}

function endVoicePress(event) {
  if (!voicePressHeld) return;
  if (voicePressPointerId !== null && event?.pointerId != null && event.pointerId !== voicePressPointerId) return;
  voicePressHeld = false;
  window.clearTimeout(voicePressTimer);
  voicePressTimer = null;
  if (voicePressKind === 'keyboard') {
    ignoreKeyboardVoiceClick = true;
    window.setTimeout(() => { ignoreKeyboardVoiceClick = false; }, 0);
  }
  if (voicePressStartedRecording) {
    releaseVoiceRecording(event);
  } else {
    toggleSpeechToText();
  }
  voicePressPointerId = null;
  voicePressKind = '';
  voicePressStartedRecording = false;
}

function cancelVoicePress() {
  const shouldCancelRecording = voicePressStartedRecording;
  voicePressHeld = false;
  voicePressPointerId = null;
  voicePressKind = '';
  voicePressStartedRecording = false;
  window.clearTimeout(voicePressTimer);
  voicePressTimer = null;
  if (shouldCancelRecording) cancelVoiceRecording();
}

function handleVoiceClick(event) {
  // Pointer gestures were handled on pointerup. detail === 0 is a keyboard or
  // assistive-technology click; keyboard press-and-release already handled it.
  if (event.detail !== 0) return;
  if (ignoreKeyboardVoiceClick) {
    ignoreKeyboardVoiceClick = false;
    return;
  }
  toggleSpeechToText();
}

function toggleSpeechToText() {
  if (props.disabled || pendingVoice.value || dictationProcessing.value) return;
  if (speechRecognition) {
    try { speechRecognition.stop(); } catch (error) { /* already stopped */ }
    return;
  }
  if (dictationRecording.value || dictationStarting.value) {
    toggleGptDictation();
    return;
  }
  if (Object.prototype.hasOwnProperty.call(DEVICE_STT_LANGUAGES, language.current)) {
    toggleDeviceSpeechRecognition();
    return;
  }
  toggleGptDictation();
}

function toggleDeviceSpeechRecognition() {
  if (speechRecognition) {
    try { speechRecognition.stop(); } catch (error) { /* already stopped */ }
    return;
  }
  const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!Recognition) {
    voiceError.value = language.t('composer.dictationUnsupported');
    return;
  }
  voiceError.value = '';
  voiceNotice.value = '';
  let recognizedText = '';
  let recognitionError = '';
  const recognition = new Recognition();
  speechRecognition = recognition;
  speechRecognitionActive.value = true;
  recognition.lang = DEVICE_STT_LANGUAGES[language.current];
  recognition.continuous = false;
  recognition.interimResults = false;
  recognition.maxAlternatives = 1;
  recognition.onstart = () => { speechRecognitionActive.value = true; };
  recognition.onresult = event => {
    for (let index = event.resultIndex; index < event.results.length; index += 1) {
      if (event.results[index].isFinal) recognizedText += event.results[index][0]?.transcript || '';
    }
  };
  recognition.onerror = event => {
    if (event.error !== 'aborted') recognitionError = event.error || 'recognition';
  };
  recognition.onend = () => {
    if (speechRecognition === recognition) {
      speechRecognition = null;
      speechRecognitionActive.value = false;
    }
    if (recognizedText.trim()) {
      insertRecognizedText(recognizedText.trim());
      voiceNotice.value = '';
    } else if (recognitionError) {
      voiceError.value = language.t('composer.dictationFailed');
      voiceNotice.value = '';
    } else {
      voiceNotice.value = '';
    }
  };
  try {
    recognition.start();
  } catch (error) {
    speechRecognition = null;
    speechRecognitionActive.value = false;
    voiceNotice.value = '';
    voiceError.value = language.t('composer.dictationFailed');
  }
}

async function toggleGptDictation() {
  if (dictationRecording.value) {
    stopGptDictation();
    return;
  }
  if (dictationStarting.value) {
    dictationStartCancelled = true;
    return;
  }
  await startGptDictation();
}

async function startGptDictation() {
  voiceError.value = '';
  voiceNotice.value = '';
  dictationStartCancelled = false;
  dictationCaptureCancelled = false;
  dictationSourceLanguage = language.current;
  if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
    voiceError.value = language.t('composer.voiceUnsupported');
    return;
  }
  dictationStarting.value = true;
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    if (dictationStartCancelled || props.disabled) {
      stream.getTracks().forEach(track => track.stop());
      dictationStarting.value = false;
      return;
    }
    dictationStream = stream;
    dictationChunks = [];
    const mimeType = supportedRecordingMimeType();
    dictationRecorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    dictationRecorder.addEventListener('dataavailable', event => {
      if (event.data?.size) dictationChunks.push(event.data);
    });
    dictationRecorder.addEventListener('stop', finishGptDictation, { once: true });
    dictationRecorder.start(250);
    dictationStartedAt = Date.now();
    dictationRecording.value = true;
    dictationStarting.value = false;
    dictationMaxTimer = window.setTimeout(() => stopGptDictation(), 60000);
  } catch (error) {
    dictationStarting.value = false;
    voiceError.value = error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError'
      ? language.t('composer.voicePermission')
      : error?.message || language.t('composer.voiceFailed');
    releaseDictationResources();
  }
}

function stopGptDictation({ cancel = false } = {}) {
  dictationCaptureCancelled = cancel;
  window.clearTimeout(dictationMaxTimer);
  dictationMaxTimer = null;
  dictationRecording.value = false;
  if (dictationRecorder && dictationRecorder.state !== 'inactive') {
    dictationRecorder.stop();
  } else {
    releaseDictationResources();
  }
}

async function finishGptDictation() {
  const duration = Date.now() - dictationStartedAt;
  const mime = dictationRecorder?.mimeType || dictationChunks[0]?.type || 'audio/webm';
  const blob = new Blob(dictationChunks, { type: mime });
  const cancelled = dictationCaptureCancelled;
  releaseDictationResources();
  if (cancelled || duration < 350 || !blob.size) {
    voiceNotice.value = '';
    if (!cancelled) voiceError.value = language.t('composer.dictationTooShort');
    return;
  }
  dictationProcessing.value = true;
  voiceNotice.value = '';
  dictationAbortController = new AbortController();
  const extension = mime.includes('mp4') ? 'm4a' : mime.includes('ogg') ? 'ogg' : 'webm';
  const audioMime = mime.split(';', 1)[0] || 'audio/webm';
  const audio = new File([blob], `dictation-${Date.now()}.${extension}`, { type: audioMime });
  try {
    const result = await transcribeVoiceMessage({
      token: session.token,
      sourceLanguage: dictationSourceLanguage,
      audio,
      filename: audio.name,
      signal: dictationAbortController.signal
    });
    const transcript = String(result?.text || '').trim();
    if (!transcript) throw new Error(language.t('composer.dictationNoResult'));
    insertRecognizedText(transcript);
    voiceNotice.value = '';
  } catch (error) {
    if (error?.name !== 'AbortError') voiceError.value = error?.message || language.t('composer.dictationFailed');
    voiceNotice.value = '';
  } finally {
    dictationAbortController = null;
    dictationProcessing.value = false;
  }
}

function releaseDictationResources() {
  window.clearTimeout(dictationMaxTimer);
  dictationMaxTimer = null;
  dictationStream?.getTracks().forEach(track => track.stop());
  dictationStream = null;
  dictationRecorder = null;
  dictationChunks = [];
}

function insertRecognizedText(value) {
  if (!value) return;
  const element = textareaEl.value;
  if (!element) {
    text.value = `${text.value}${text.value ? ' ' : ''}${value}`;
    return;
  }
  const start = element.selectionStart ?? text.value.length;
  const end = element.selectionEnd ?? text.value.length;
  const prefix = text.value.slice(0, start);
  const suffix = text.value.slice(end);
  const separator = prefix && !/\s$/.test(prefix) ? ' ' : '';
  text.value = `${prefix}${separator}${value}${suffix}`;
  requestAnimationFrame(() => {
    element.focus();
    const cursor = prefix.length + separator.length + value.length;
    element.setSelectionRange(cursor, cursor);
  });
}

function sendPendingVoice() {
  if (props.disabled || !pendingVoice.value) return;
  const voice = pendingVoice.value;
  pendingVoice.value = null;
  emit('send-voice', { file: voice.file, duration: voice.duration });
  URL.revokeObjectURL(voice.url);
  if (voiceStopReason) {
    voiceNotice.value = voiceStopReason;
    voiceStopReason = '';
  }
}

function discardPendingVoice() {
  if (!pendingVoice.value) return;
  URL.revokeObjectURL(pendingVoice.value.url);
  pendingVoice.value = null;
  voiceStopReason = '';
}

function formatVoiceDuration(milliseconds) {
  const seconds = Math.max(0, Math.floor(milliseconds / 1000));
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}

function supportedRecordingMimeType() {
  if (!window.MediaRecorder?.isTypeSupported) return '';
  return ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']
    .find(type => window.MediaRecorder.isTypeSupported(type)) || '';
}

async function startVoiceRecording(event) {
  if (props.disabled || voiceStarting.value || voiceRecording.value) return;
  voiceError.value = '';
  voiceNotice.value = '';
  voiceStopReason = '';
  voicePointerHeld = true;
  voiceShouldPreview = false;
  voicePointerId = event?.pointerId ?? null;
  try {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      throw new Error(language.t('composer.voiceUnsupported'));
    }
    voiceStarting.value = true;
    if (voicePointerId !== null) event.currentTarget?.setPointerCapture?.(voicePointerId);
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    if (!voicePointerHeld || props.disabled) {
      stream.getTracks().forEach(track => track.stop());
      voiceStarting.value = false;
      if (!props.disabled) voiceNotice.value = language.t('composer.voicePressAgain');
      return;
    }
    voiceStream = stream;
    const mimeType = supportedRecordingMimeType();
    voiceRecorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    voiceChunks = [];
    voiceStartedAt = Date.now();
    voiceRecorder.addEventListener('dataavailable', eventData => {
      if (eventData.data?.size) voiceChunks.push(eventData.data);
    });
    voiceRecorder.addEventListener('stop', finishVoiceRecording, { once: true });
    voiceRecorder.start(250);
    voiceRecording.value = true;
    voiceStarting.value = false;
    voiceClock = window.setInterval(() => {
      voiceDurationLabel.value = formatVoiceDuration(Date.now() - voiceStartedAt);
    }, 250);
    voiceMaxTimer = window.setTimeout(() => {
      voicePointerHeld = false;
      voiceShouldPreview = true;
      voiceStopReason = language.t('composer.voiceLimitPreview');
      stopVoiceRecorder();
    }, 60000);
  } catch (error) {
    voicePointerHeld = false;
    voiceStarting.value = false;
    voiceError.value = error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError'
      ? language.t('composer.voicePermission')
      : error?.message || language.t('composer.voiceFailed');
    releaseVoiceResources();
  }
}

function releaseVoiceRecording(event) {
  if (voicePointerId !== null && event?.pointerId != null && event.pointerId !== voicePointerId) return;
  voicePointerHeld = false;
  voicePointerId = null;
  if (voiceStarting.value) return;
  if (!voiceRecorder || voiceRecorder.state === 'inactive') return;
  voiceShouldPreview = true;
  stopVoiceRecorder();
}

function cancelVoiceRecording() {
  if (!voicePointerHeld && !voiceStarting.value && !voiceRecording.value) return;
  voicePointerHeld = false;
  voicePointerId = null;
  voiceShouldPreview = false;
  if (voiceRecorder && voiceRecorder.state !== 'inactive') {
    stopVoiceRecorder();
  } else {
    releaseVoiceResources();
    voiceStarting.value = false;
  }
}

function stopVoiceRecorder() {
  window.clearTimeout(voiceMaxTimer);
  voiceMaxTimer = null;
  if (voiceRecorder && voiceRecorder.state !== 'inactive') voiceRecorder.stop();
  else releaseVoiceResources();
}

function finishVoiceRecording() {
  const durationMs = Math.max(0, Date.now() - voiceStartedAt);
  const duration = Math.max(1, Math.ceil(durationMs / 1000));
  const mime = voiceRecorder?.mimeType || voiceChunks[0]?.type || 'audio/webm';
  const blob = new Blob(voiceChunks, { type: mime });
  const shouldPreview = voiceShouldPreview;
  const stopReason = voiceStopReason;
  voiceShouldPreview = false;
  voiceRecording.value = false;
  voiceStarting.value = false;
  voiceDurationLabel.value = '00:00';
  releaseVoiceResources();
  if (!shouldPreview) {
    voiceStopReason = '';
    return;
  }
  if (durationMs < 700 || !blob.size) {
    voiceError.value = language.t('composer.voiceTooShort');
    voiceStopReason = '';
    return;
  }
  const extension = mime.includes('mp4') ? 'm4a' : mime.includes('ogg') ? 'ogg' : 'webm';
  const file = new File([blob], `voice-${Date.now()}.${extension}`, { type: mime });
  pendingVoice.value = { file, duration, url: URL.createObjectURL(blob) };
  if (stopReason) voiceNotice.value = stopReason;
  voiceStopReason = stopReason;
}

function releaseVoiceResources() {
  window.clearInterval(voiceClock);
  window.clearTimeout(voiceMaxTimer);
  voiceClock = null;
  voiceMaxTimer = null;
  voiceStream?.getTracks().forEach(track => track.stop());
  voiceStream = null;
  voiceRecorder = null;
  voiceChunks = [];
}

function toggleEmojiPicker() {
  emojiOpen.value = !emojiOpen.value;
}

function insertEmoji(emoji) {
  const element = textareaEl.value;
  if (!element) {
    text.value += emoji;
    return;
  }
  const start = element.selectionStart ?? text.value.length;
  const end = element.selectionEnd ?? text.value.length;
  text.value = `${text.value.slice(0, start)}${emoji}${text.value.slice(end)}`;
  requestAnimationFrame(() => {
    element.focus();
    const cursor = start + emoji.length;
    element.setSelectionRange(cursor, cursor);
  });
}

function chooseImage() {
  imageInput.value?.click();
}

function chooseFile() {
  fileInput.value?.click();
}

function attachmentTypeForFile(file, fallbackType = 'file') {
  if (file?.type?.startsWith('image/')) {
    return 'image';
  }
  return fallbackType === 'image' ? 'image' : 'file';
}

function emitFiles(files, fallbackType = 'file') {
  if (props.disabled) {
    return;
  }
  const validFiles = Array.from(files || []).filter(file => file instanceof File && file.size > 0);
  if (!validFiles.length) {
    return;
  }

  const caption = text.value.trim();
  validFiles.forEach((file, index) => {
    emit('send-attachment', {
      type: attachmentTypeForFile(file, fallbackType),
      file,
      text: index === 0 ? caption : ''
    });
  });
  text.value = '';
  emojiOpen.value = false;
}

function stageImages(files) {
  if (props.disabled) return;
  const images = Array.from(files || []).filter(file => file instanceof File && file.size > 0 && file.type.startsWith('image/'));
  if (!images.length) return;
  if (!pendingImages.value.length) {
    previewCaption.value = text.value.trim();
    text.value = '';
  }
  pendingImages.value.push(...images.map(file => ({ file, url: URL.createObjectURL(file) })));
  activeImageIndex.value = pendingImages.value.length - images.length;
  emojiOpen.value = false;
}

function removePendingImage(index) {
  const [removed] = pendingImages.value.splice(index, 1);
  if (removed?.url) URL.revokeObjectURL(removed.url);
  activeImageIndex.value = Math.max(0, Math.min(activeImageIndex.value, pendingImages.value.length - 1));
  if (!pendingImages.value.length) previewCaption.value = '';
}

function cancelImagePreview() {
  pendingImages.value.forEach(item => URL.revokeObjectURL(item.url));
  pendingImages.value = [];
  activeImageIndex.value = 0;
  previewCaption.value = '';
}

function sendPendingImages() {
  if (props.disabled || !pendingImages.value.length) return;
  const images = pendingImages.value.map(item => item.file);
  const caption = previewCaption.value.trim();
  pendingImages.value.forEach(item => URL.revokeObjectURL(item.url));
  pendingImages.value = [];
  activeImageIndex.value = 0;
  previewCaption.value = '';
  images.forEach((file, index) => emit('send-attachment', { type: 'image', file, text: index === images.length - 1 ? caption : '' }));
}

function selectAttachment(event, type) {
  const input = event.target;
  const files = input.files ? Array.from(input.files) : [];
  input.value = '';
  if (!files.length || props.disabled) {
    return;
  }
  if (type === 'image') {
    stageImages(files);
    return;
  }
  emitFiles(files, type);
}

function hasDraggedFiles(event) {
  const types = Array.from(event.dataTransfer?.types || []);
  return types.includes('Files');
}

function handleDragEnter(event) {
  if (props.disabled || !hasDraggedFiles(event)) {
    return;
  }
  dragDepth += 1;
  dropActive.value = true;
  event.dataTransfer.dropEffect = 'copy';
}

function handleDragOver(event) {
  if (props.disabled || !hasDraggedFiles(event)) {
    return;
  }
  dropActive.value = true;
  event.dataTransfer.dropEffect = 'copy';
}

function handleDragLeave() {
  dragDepth = Math.max(0, dragDepth - 1);
  if (dragDepth === 0) {
    dropActive.value = false;
  }
}

function filesFromDataTransfer(dataTransfer) {
  const items = Array.from(dataTransfer?.items || []);
  const files = items
    .filter(item => item.kind === 'file' && !item.webkitGetAsEntry?.()?.isDirectory)
    .map(item => item.getAsFile())
    .filter(Boolean);
  if (files.length) {
    return files;
  }
  return Array.from(dataTransfer?.files || []);
}

function handleDrop(event) {
  dragDepth = 0;
  dropActive.value = false;
  if (props.disabled) {
    return;
  }
  const files = filesFromDataTransfer(event.dataTransfer);
  stageImages(files.filter(file => file.type.startsWith('image/')));
  emitFiles(files.filter(file => !file.type.startsWith('image/')));
}

function imageFilesFromClipboard(clipboardData) {
  return Array.from(clipboardData?.items || [])
    .filter(item => item.kind === 'file' && item.type.startsWith('image/'))
    .map(item => item.getAsFile())
    .filter(Boolean);
}

function handlePaste(event) {
  if (props.disabled) {
    return;
  }
  const imageFiles = imageFilesFromClipboard(event.clipboardData);
  if (!imageFiles.length) {
    return;
  }
  event.preventDefault();
  stageImages(imageFiles);
}

function handlePreviewPaste(event) {
  if (!pendingImages.value.length || event.defaultPrevented) return;
  const imageFiles = imageFilesFromClipboard(event.clipboardData);
  if (!imageFiles.length) return;
  event.preventDefault();
  stageImages(imageFiles);
}

function handlePreviewDragOver(event) {
  if (!pendingImages.value.length || !hasDraggedFiles(event)) return;
  event.preventDefault();
  previewDropActive.value = true;
  event.dataTransfer.dropEffect = 'copy';
}

function handlePreviewDragLeave(event) {
  if (!event.relatedTarget) previewDropActive.value = false;
}

function handlePreviewDrop(event) {
  if (!pendingImages.value.length || event.defaultPrevented) return;
  const images = filesFromDataTransfer(event.dataTransfer).filter(file => file.type.startsWith('image/'));
  previewDropActive.value = false;
  if (!images.length) return;
  event.preventDefault();
  stageImages(images);
}

onMounted(() => {
  window.addEventListener('paste', handlePreviewPaste);
  window.addEventListener('dragover', handlePreviewDragOver);
  window.addEventListener('dragleave', handlePreviewDragLeave);
  window.addEventListener('drop', handlePreviewDrop);
});

onBeforeUnmount(() => {
  window.clearTimeout(voicePressTimer);
  voicePressHeld = false;
  if (speechRecognition) {
    try { speechRecognition.abort(); } catch (error) { /* already stopped */ }
    speechRecognition = null;
  }
  dictationStartCancelled = true;
  dictationAbortController?.abort();
  stopGptDictation({ cancel: true });
  cancelVoiceRecording();
  discardPendingVoice();
  cancelImagePreview();
  window.removeEventListener('paste', handlePreviewPaste);
  window.removeEventListener('dragover', handlePreviewDragOver);
  window.removeEventListener('dragleave', handlePreviewDragLeave);
  window.removeEventListener('drop', handlePreviewDrop);
});
</script>
